<template>
  <teleport to="body">
    <div v-if="open" class="core-name-backdrop" @click.self="$emit('close')">
      <form
        class="core-modal-surface bg-white p-6 w-[480px] max-w-full border border-neutral-300"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @submit.prevent="submit">
        <h2 class="mb-2 text-xl font-semibold text-neutral-900">{{ title }}</h2>
        <p v-if="description" class="mb-4 text-sm text-neutral-550">{{ description }}</p>
        <label class="block mb-1 text-sm text-neutral-700" :for="inputId">
          {{ t('project.summary.state.name_label') }}
        </label>
        <input
          :id="inputId"
          ref="inputRef"
          v-model="name"
          type="text"
          class="core-form-input w-full"
          :maxlength="MAX_LENGTH"
          data-testid="project-state-name-input" />
        <div class="mt-6 flex gap-2">
          <button
            type="submit"
            class="core-action-button core-action-button--primary h-11 flex-1"
            :disabled="!name.trim()"
            data-testid="project-state-name-confirm">
            {{ confirmLabel }}
          </button>
          <button
            type="button"
            class="core-action-button core-action-button--supporting h-11 flex-1"
            @click="$emit('close')">
            {{ t('general.button.cancel') }}
          </button>
        </div>
      </form>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { nextTick, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/** Asks for the name of a project state (when saving or renaming). */
const MAX_LENGTH = 200

const props = defineProps<{
  open: boolean
  title: string
  description?: string
  confirmLabel: string
  initialValue?: string
}>()
const emit = defineEmits<{
  (e: 'confirm', name: string): void
  (e: 'close'): void
}>()

const { t } = useI18n()
const inputId = useId()
const inputRef = ref<HTMLInputElement | null>(null)
const name = ref('')

watch(
  () => props.open,
  async (open) => {
    if (!open) return
    name.value = props.initialValue ?? ''
    await nextTick()
    inputRef.value?.focus()
    inputRef.value?.select()
  }
)

function submit() {
  const value = name.value.trim()
  if (value) emit('confirm', value)
}
</script>

<style lang="postcss" scoped>
.core-name-backdrop {
  @apply fixed inset-0 overflow-y-auto overflow-x-hidden;
  @apply z-70 flex items-center justify-center;
  @apply bg-neutral-100/15 backdrop-blur-[2px];
}

.core-modal-surface {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2);
}
</style>
